import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface HubPerformance {
  hub_id: string;
  hub_name: string;
  capacity: number;
  total_profissionais: number;
  total_obras: number;
  inspecoes_agendadas: number;
  inspecoes_concluidas: number;
  inspecoes_atrasadas: number;
  taxa_conclusao: number;
  utilizacao_capacidade: number;
}

export interface RelatorioGovernanca {
  hub_id: string;
  hub_name: string;
  mes_referencia: string;
  status: string;
  dias_em_rascunho: number;
  sem_indicadores: boolean;
  prazo_vencido: boolean;
  total_indicadores: number;
}

export interface ProfissionalPerformance {
  professional_id: string;
  professional_name: string;
  hub_id: string;
  hub_name: string;
  role: string;
  total_agendadas: number;
  total_concluidas: number;
  taxa_conclusao: number;
  tempo_medio_minutos: number | null;
  dias_trabalhados: number;
}

export interface InspecaoAtrasada {
  agenda_id: string;
  hub_id: string;
  hub_name: string;
  work_name: string;
  professional_name: string;
  dias_atraso: number;
  scheduled_date: string;
}

export interface GestorMetrics {
  hubsPerformance: HubPerformance[];
  relatoriosGovernanca: RelatorioGovernanca[];
  profissionaisPerformance: ProfissionalPerformance[];
  inspecoesAtrasadas: InspecaoAtrasada[];
  totais: {
    totalHubs: number;
    totalProfissionais: number;
    totalObras: number;
    totalInspecoesAgendadas: number;
    totalInspecoesConcluidas: number;
    totalInspecoesAtrasadas: number;
    taxaConclusaoGeral: number;
    relatoriosPendentes: number;
    relatoriosValidados: number;
  };
}

export function useGestorPerformance() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<GestorMetrics | null>(null);
  const { toast } = useToast();

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      // Buscar performance dos hubs via view de capacidade e utilização
      let hubsPerformance: HubPerformance[] = [];
      
      // Usar a nova view que calcula capacidade e demanda corretamente
      const { data: hubsCapacidade, error: capacidadeError } = await supabase
        .from('vw_hub_capacidade_utilizacao')
        .select('*')
        .eq('hub_status', 'active');
      
      if (!capacidadeError && hubsCapacidade) {
        const hubIds = hubsCapacidade.map(h => h.hub_id);
        
        // Buscar dados de inspeções do MÊS ATUAL apenas com PAGINAÇÃO
        // (Supabase tem limite de 1000 rows por query)
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        
        // Buscar todas as inspeções com paginação
        const allInspecoes: { id: string; hub_id: string; status: string; scheduled_date: string }[] = [];
        const pageSize = 1000;
        let offset = 0;
        let hasMore = true;
        
        while (hasMore) {
          const { data: pageData, error: pageError } = await supabase
            .from('agenda_inspecoes')
            .select('id, hub_id, status, scheduled_date')
            .in('hub_id', hubIds)
            .gte('scheduled_date', monthStart)
            .lte('scheduled_date', monthEnd)
            .eq('is_test', false)
            .range(offset, offset + pageSize - 1);
          
          if (pageError) {
            console.error('Erro ao buscar inspeções:', pageError);
            break;
          }
          
          const rows = pageData || [];
          allInspecoes.push(...rows);
          
          if (rows.length < pageSize) {
            hasMore = false;
          } else {
            offset += pageSize;
          }
        }

        // Data de hoje sem hora (início do dia) para comparação D+1
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const inspecoesPorHub = allInspecoes.reduce((acc, i) => {
          if (!acc[i.hub_id]) {
            acc[i.hub_id] = { agendadas: 0, concluidas: 0, atrasadas: 0 };
          }
          acc[i.hub_id].agendadas++;
          if (i.status === 'completed') {
            acc[i.hub_id].concluidas++;
          }
          // Regra D+1: só é atraso se scheduled_date < hoje (não inclui hoje)
          const scheduledDate = new Date(i.scheduled_date + 'T00:00:00');
          if (i.status === 'scheduled' && scheduledDate < today) {
            acc[i.hub_id].atrasadas++;
          }
          return acc;
        }, {} as Record<string, { agendadas: number; concluidas: number; atrasadas: number }>);

        hubsPerformance = hubsCapacidade.map(h => {
          const insp = inspecoesPorHub[h.hub_id] || { agendadas: 0, concluidas: 0, atrasadas: 0 };
          const taxa = insp.agendadas > 0 ? (insp.concluidas / insp.agendadas) * 100 : 0;
          return {
            hub_id: h.hub_id,
            hub_name: h.hub_name,
            capacity: h.capacidade_calculada, // Soma de daily_capacity dos profissionais
            total_profissionais: h.total_profissionais,
            total_obras: h.obras_hub_principal, // Obras efetivamente atendidas pelo hub (corrigido)
            inspecoes_agendadas: insp.agendadas,
            inspecoes_concluidas: insp.concluidas,
            inspecoes_atrasadas: insp.atrasadas,
            taxa_conclusao: Math.round(taxa),
            utilizacao_capacidade: Math.round(Number(h.taxa_utilizacao) || 0) // obras_hub_principal / capacidade
          };
        });
      }

      // Buscar relatórios críticos (governança)
      const { data: relatoriosData } = await supabase
        .from('vw_relatorios_criticos')
        .select('*')
        .order('mes_referencia', { ascending: false });

      const relatoriosGovernanca: RelatorioGovernanca[] = (relatoriosData || []).map(r => ({
        hub_id: r.hub_id || '',
        hub_name: r.hub_name || '',
        mes_referencia: r.mes_referencia || '',
        status: r.status || '',
        dias_em_rascunho: r.dias_em_rascunho || 0,
        sem_indicadores: r.sem_indicadores || false,
        prazo_vencido: r.prazo_vencido || false,
        total_indicadores: Number(r.total_indicadores) || 0
      }));

      // Buscar performance dos profissionais
      const { data: profData } = await supabase
        .from('vw_performance_profissional')
        .select('*')
        .order('total_concluidas', { ascending: false });

      const profissionaisPerformance: ProfissionalPerformance[] = (profData || []).map(p => ({
        professional_id: p.professional_id || '',
        professional_name: p.professional_name || '',
        hub_id: p.hub_id || '',
        hub_name: p.hub_name || '',
        role: p.role || '',
        total_agendadas: Number(p.total_agendadas) || 0,
        total_concluidas: Number(p.total_concluidas) || 0,
        taxa_conclusao: p.total_agendadas > 0 
          ? Math.round((Number(p.total_concluidas) / Number(p.total_agendadas)) * 100) 
          : 0,
        tempo_medio_minutos: p.tempo_medio_minutos ? Number(p.tempo_medio_minutos) : null,
        dias_trabalhados: Number(p.dias_trabalhados) || 0
      }));

      // Buscar inspeções atrasadas
      const { data: atrasadasData } = await supabase
        .from('vw_inspecoes_atrasadas')
        .select('*')
        .order('dias_atraso', { ascending: false })
        .limit(50);

      const inspecoesAtrasadas: InspecaoAtrasada[] = (atrasadasData || []).map(a => ({
        agenda_id: a.agenda_id || '',
        hub_id: a.hub_id || '',
        hub_name: a.hub_name || '',
        work_name: a.work_name || '',
        professional_name: a.professional_name || '',
        dias_atraso: a.dias_atraso || 0,
        scheduled_date: a.scheduled_date || ''
      }));

      // Calcular totais
      const totalInspecoesAgendadas = hubsPerformance.reduce((sum, h) => sum + h.inspecoes_agendadas, 0);
      const totalInspecoesConcluidas = hubsPerformance.reduce((sum, h) => sum + h.inspecoes_concluidas, 0);
      
      // Total de obras: somar obras_hub_principal de todos os hubs (já sem duplicação)
      const totalObrasDistinct = hubsPerformance.reduce((sum, h) => sum + h.total_obras, 0);
      
      const totais = {
        totalHubs: hubsPerformance.length,
        totalProfissionais: hubsPerformance.reduce((sum, h) => sum + h.total_profissionais, 0),
        totalObras: totalObrasDistinct, // Corrigido: soma por cluster distinto
        totalInspecoesAgendadas,
        totalInspecoesConcluidas,
        totalInspecoesAtrasadas: inspecoesAtrasadas.length,
        taxaConclusaoGeral: totalInspecoesAgendadas > 0 
          ? Math.round((totalInspecoesConcluidas / totalInspecoesAgendadas) * 100)
          : 0,
        relatoriosPendentes: relatoriosGovernanca.filter(r => r.status !== 'validado').length,
        relatoriosValidados: relatoriosGovernanca.filter(r => r.status === 'validado').length
      };

      setMetrics({
        hubsPerformance,
        relatoriosGovernanca,
        profissionaisPerformance,
        inspecoesAtrasadas,
        totais
      });

    } catch (error: any) {
      console.error('Erro ao buscar métricas:', error);
      toast({
        title: 'Erro ao carregar métricas',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    loading,
    metrics,
    refetch: fetchMetrics
  };
}
